import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import type { Scene } from "@babylonjs/core/scene";
import { PALETTE } from "../assets/palettes";
import type { ProceduralTextureKind } from "../assets/TextureGenerator";
import type { GridMap } from "../generation/GridMap";
import { CELL_SIZE, TileType, type LevelData, type Point, type Room } from "../generation/types";
import type { MaterialFactory } from "./MaterialFactory";
import { gridToWorld } from "./worldCoordinates";
import { applyBoxQuadUvs, type BoxDimensions } from "./UvTiling";
import { restrictLightToNearbyMeshes } from "./LocalLightMeshSelector";
import type { LoadingTask } from "../core/LoadingTask";

const WALL_HEIGHT = 3.6;
const WALL_THICKNESS = 0.24;
const GROUND_CHUNK_CELLS = 8;

export interface LevelMeshes {
  readonly colliders: readonly Mesh[];
  readonly decorativeLights: readonly PointLight[];
}

export class LevelMeshBuilder {
  private readonly colliders: Mesh[] = [];
  private readonly decorativeLights: PointLight[] = [];

  public constructor(
    private readonly scene: Scene,
    private readonly materials: MaterialFactory,
  ) {}

  public build(level: LevelData): LevelMeshes {
    for (const step of this.buildSteps(level)) {
      void step;
    }
    return { colliders: this.colliders, decorativeLights: this.decorativeLights };
  }

  public async buildAsync(level: LevelData, task: LoadingTask): Promise<LevelMeshes> {
    await task.drain(this.buildSteps(level));
    return { colliders: this.colliders, decorativeLights: this.decorativeLights };
  }

  private *buildSteps(level: LevelData): Generator<void> {
    yield* this.createGroundAndCeiling(level);
    if (level.mode === "procedural") this.createRoomSurfaces(level);
    yield;
    yield* this.createWallRuns(level);
    if (level.mode === "mission") this.createMissionDecorations(level);
    else this.createDecorations(level);
    yield;
    for (const light of this.decorativeLights) {
      restrictLightToNearbyMeshes(light, this.scene.meshes, light.position, light.range);
    }
  }

  private *createGroundAndCeiling(level: LevelData): Generator<void> {
    for (let startY = 0; startY < level.grid.height; startY += GROUND_CHUNK_CELLS) {
      for (let startX = 0; startX < level.grid.width; startX += GROUND_CHUNK_CELLS) {
        const cellWidth = Math.min(GROUND_CHUNK_CELLS, level.grid.width - startX);
        const cellDepth = Math.min(GROUND_CHUNK_CELLS, level.grid.height - startY);
        const dimensions = {
          width: cellWidth * CELL_SIZE,
          depth: cellDepth * CELL_SIZE,
          height: 0.12,
        };
        const firstCell = gridToWorld({ x: startX, y: startY }, level.grid);
        const centerX = firstCell.x + ((cellWidth - 1) * CELL_SIZE) / 2;
        const centerZ = firstCell.z + ((cellDepth - 1) * CELL_SIZE) / 2;
        const suffix = `${startX}-${startY}`;

        const floor = MeshBuilder.CreateBox(`base-floor-${suffix}`, dimensions, this.scene);
        floor.position.set(centerX, -0.06, centerZ);
        applyBoxQuadUvs(floor, dimensions, 4, 4);
        floor.material = this.materials.getFloor("darkStoneFloor");
        floor.metadata = { surface: "floor", lightReceiverChunk: true };

        const ceiling = MeshBuilder.CreateBox(`ceiling-${suffix}`, dimensions, this.scene);
        ceiling.position.set(centerX, WALL_HEIGHT + 0.06, centerZ);
        applyBoxQuadUvs(ceiling, dimensions, 4, 4);
        ceiling.material = this.materials.get("overgrownCeilingTile");
        ceiling.metadata = { surface: "ceiling", lightReceiverChunk: true };
        yield;
      }
    }
  }

  private createRoomSurfaces(level: LevelData): void {
    for (const room of level.rooms) {
      const centre = gridToWorld(
        {
          x: room.x + (room.width - 1) / 2,
          y: room.y + (room.height - 1) / 2,
        },
        level.grid,
      );
      const dimensions = {
        width: room.width * CELL_SIZE,
        depth: room.height * CELL_SIZE,
        height: 0.04,
      };
      const surface = MeshBuilder.CreateBox(`room-floor-${room.id}`, dimensions, this.scene);
      surface.position.set(centre.x, 0.01, centre.z);
      applyBoxQuadUvs(surface, dimensions, 4, 4);
      surface.material = this.materials.getFloor(
        room.id === "toxic" ? "metalPanels" : "darkStoneFloor",
      );
      surface.metadata = { surface: "floor", roomId: room.id };
    }

    for (const row of this.collectTileRuns(level.grid, TileType.Toxic)) {
      const start = gridToWorld({ x: row.startX, y: row.y }, level.grid);
      const dimensions = { width: row.length * CELL_SIZE, depth: CELL_SIZE, height: 0.08 };
      const toxic = MeshBuilder.CreateBox(`toxic-${row.y}-${row.startX}`, dimensions, this.scene);
      toxic.position.set(start.x + ((row.length - 1) * CELL_SIZE) / 2, 0.07, start.z);
      applyBoxQuadUvs(toxic, dimensions, 4, 4);
      toxic.material = this.materials.getFloor("toxicFloor", true);
      toxic.metadata = { surface: "toxic" };
    }
  }

  private *createWallRuns(level: LevelData): Generator<void> {
    const grid = level.grid;
    for (let y = 0; y < grid.height; y += 1) {
      this.scanHorizontalBoundary(grid, y, -1);
      this.scanHorizontalBoundary(grid, y, 1);
      yield;
    }
    for (let x = 0; x < grid.width; x += 1) {
      this.scanVerticalBoundary(grid, x, -1);
      this.scanVerticalBoundary(grid, x, 1);
      yield;
    }
    if (level.mode === "mission") this.createAuthoredInternalWallRuns(grid);
  }

  private createAuthoredInternalWallRuns(grid: GridMap): void {
    for (let boundaryY = 1; boundaryY < grid.height; boundaryY += 1) {
      let runStart = -1;
      for (let x = 0; x <= grid.width; x += 1) {
        const blocked =
          x < grid.width &&
          grid.isWalkable({ x, y: boundaryY - 1 }) &&
          grid.isWalkable({ x, y: boundaryY }) &&
          grid.isEdgeBlocked({ x, y: boundaryY - 1 }, { x, y: boundaryY });
        if (blocked && runStart < 0) runStart = x;
        else if (!blocked && runStart >= 0) {
          this.createAuthoredHorizontalWall(grid, runStart, x - 1, boundaryY);
          runStart = -1;
        }
      }
    }
    for (let boundaryX = 1; boundaryX < grid.width; boundaryX += 1) {
      let runStart = -1;
      for (let y = 0; y <= grid.height; y += 1) {
        const blocked =
          y < grid.height &&
          grid.isWalkable({ x: boundaryX - 1, y }) &&
          grid.isWalkable({ x: boundaryX, y }) &&
          grid.isEdgeBlocked({ x: boundaryX - 1, y }, { x: boundaryX, y });
        if (blocked && runStart < 0) runStart = y;
        else if (!blocked && runStart >= 0) {
          this.createAuthoredVerticalWall(grid, boundaryX, runStart, y - 1);
          runStart = -1;
        }
      }
    }
  }

  private createAuthoredHorizontalWall(
    grid: GridMap,
    startX: number,
    endX: number,
    boundaryY: number,
  ): void {
    const length = endX - startX + 1;
    const start = gridToWorld({ x: startX, y: boundaryY }, grid);
    const mesh = MeshBuilder.CreateBox(
      `wall-authored-h-${boundaryY}-${startX}`,
      { width: length * CELL_SIZE, height: WALL_HEIGHT, depth: WALL_THICKNESS },
      this.scene,
    );
    mesh.position.set(
      start.x + ((length - 1) * CELL_SIZE) / 2,
      WALL_HEIGHT / 2,
      start.z - CELL_SIZE * 0.5,
    );
    this.configureWall(mesh, this.selectDungeonWallMaterial("horizontal", boundaryY, startX), {
      width: length * CELL_SIZE,
      height: WALL_HEIGHT,
      depth: WALL_THICKNESS,
    });
  }

  private createAuthoredVerticalWall(
    grid: GridMap,
    boundaryX: number,
    startY: number,
    endY: number,
  ): void {
    const length = endY - startY + 1;
    const start = gridToWorld({ x: boundaryX, y: startY }, grid);
    const mesh = MeshBuilder.CreateBox(
      `wall-authored-v-${boundaryX}-${startY}`,
      { width: WALL_THICKNESS, height: WALL_HEIGHT, depth: length * CELL_SIZE },
      this.scene,
    );
    mesh.position.set(
      start.x - CELL_SIZE * 0.5,
      WALL_HEIGHT / 2,
      start.z + ((length - 1) * CELL_SIZE) / 2,
    );
    this.configureWall(mesh, this.selectDungeonWallMaterial("vertical", boundaryX, startY), {
      width: WALL_THICKNESS,
      height: WALL_HEIGHT,
      depth: length * CELL_SIZE,
    });
  }

  private scanHorizontalBoundary(grid: GridMap, y: number, direction: -1 | 1): void {
    let runStart = -1;
    for (let x = 0; x <= grid.width; x += 1) {
      const boundary =
        x < grid.width &&
        grid.get({ x, y }) === TileType.Wall &&
        grid.isWalkable({ x, y: y + direction });
      if (boundary && runStart < 0) {
        runStart = x;
      } else if (!boundary && runStart >= 0) {
        this.createHorizontalWall(grid, runStart, x - 1, y, direction);
        runStart = -1;
      }
    }
  }

  private scanVerticalBoundary(grid: GridMap, x: number, direction: -1 | 1): void {
    let runStart = -1;
    for (let y = 0; y <= grid.height; y += 1) {
      const boundary =
        y < grid.height &&
        grid.get({ x, y }) === TileType.Wall &&
        grid.isWalkable({ x: x + direction, y });
      if (boundary && runStart < 0) {
        runStart = y;
      } else if (!boundary && runStart >= 0) {
        this.createVerticalWall(grid, x, runStart, y - 1, direction);
        runStart = -1;
      }
    }
  }

  private createHorizontalWall(
    grid: GridMap,
    startX: number,
    endX: number,
    y: number,
    direction: -1 | 1,
  ): void {
    const length = endX - startX + 1;
    const start = gridToWorld({ x: startX, y }, grid);
    const mesh = MeshBuilder.CreateBox(
      `wall-h-${y}-${startX}-${direction}`,
      { width: length * CELL_SIZE, height: WALL_HEIGHT, depth: WALL_THICKNESS },
      this.scene,
    );
    mesh.position.set(
      start.x + ((length - 1) * CELL_SIZE) / 2,
      WALL_HEIGHT / 2,
      start.z + direction * CELL_SIZE * 0.5,
    );
    this.configureWall(mesh, this.selectDungeonWallMaterial("horizontal", y, startX), {
      width: length * CELL_SIZE,
      height: WALL_HEIGHT,
      depth: WALL_THICKNESS,
    });
  }

  private createVerticalWall(
    grid: GridMap,
    x: number,
    startY: number,
    endY: number,
    direction: -1 | 1,
  ): void {
    const length = endY - startY + 1;
    const start = gridToWorld({ x, y: startY }, grid);
    const mesh = MeshBuilder.CreateBox(
      `wall-v-${x}-${startY}-${direction}`,
      { width: WALL_THICKNESS, height: WALL_HEIGHT, depth: length * CELL_SIZE },
      this.scene,
    );
    mesh.position.set(
      start.x + direction * CELL_SIZE * 0.5,
      WALL_HEIGHT / 2,
      start.z + ((length - 1) * CELL_SIZE) / 2,
    );
    this.configureWall(mesh, this.selectDungeonWallMaterial("vertical", x, startY), {
      width: WALL_THICKNESS,
      height: WALL_HEIGHT,
      depth: length * CELL_SIZE,
    });
  }

  private configureWall(
    mesh: Mesh,
    material: ProceduralTextureKind,
    dimensions: BoxDimensions,
  ): void {
    applyBoxQuadUvs(mesh, dimensions);
    mesh.material = this.materials.get(material);
    mesh.checkCollisions = true;
    mesh.isPickable = true;
    mesh.metadata = { surface: "wall", uvMapping: "per-quad" };
    this.colliders.push(mesh);
  }

  private selectDungeonWallMaterial(
    axis: "horizontal" | "vertical",
    boundary: number,
    runStart: number,
  ): ProceduralTextureKind {
    const signature = boundary * 31 + runStart * 17 + (axis === "horizontal" ? 7 : 13);
    if (signature % 3 === 0 || signature % 5 === 0) return "overgrownBrickWall";
    if (signature % 4 === 0) return "dampBrickWall";
    if (signature % 11 === 0) return "rustedRivets";
    if (signature % 13 === 0) return "grimyStoneBlocks";
    if (signature % 7 === 0) return "sootBrickWall";
    if (signature % 4 === 0) return "medievalBrickDamaged";
    return "medievalBrickWall";
  }

  private createMissionDecorations(level: LevelData): void {
    const mission = level.mission;
    if (!mission) throw new Error("Mission level is missing authored metadata");

    for (const cell of mission.decorativeCells.cableTrenches) {
      this.createFloorInsert(level, cell, "cableTrenchFloor", false);
    }
    for (const cell of mission.decorativeCells.greenChannels) {
      this.createFloorInsert(level, cell, "greenEnergyChannel", true);
    }
    for (const cell of mission.decorativeCells.rubble) this.createMissionRubble(level, cell);
    for (const cell of mission.decorativeCells.amberLights) {
      this.createMissionLamp(level, cell, "amber");
    }
    for (const cell of mission.decorativeCells.redLights) {
      this.createMissionLamp(level, cell, "red");
    }

    this.createReactorArchitecture(level);
    this.createFoundryArchitecture(level);
    for (const gate of mission.gates)
      this.createPortalFrame(level, gate.position, gate.orientation);
    for (const terminal of mission.objects.filter(
      (object) => object.kind === "tutorial_record" || object.kind === "bell_terminal",
    )) {
      this.createMissionTerminal(level, terminal.cell);
    }
  }

  private createFloorInsert(
    level: LevelData,
    cell: Point,
    material: "cableTrenchFloor" | "greenEnergyChannel",
    emissive: boolean,
  ): void {
    const world = gridToWorld(cell, level.grid);
    const dimensions = { width: 1.55, depth: 1.55, height: 0.065 };
    const insert = MeshBuilder.CreateBox(`${material}-${cell.x}-${cell.y}`, dimensions, this.scene);
    insert.position.set(world.x, 0.045, world.z);
    applyBoxQuadUvs(insert, dimensions, 1.55, 1.55);
    insert.material = this.materials.getFloor(material, emissive);
    insert.metadata = { surface: "floor", missionDecoration: material };
  }

  private createMissionRubble(level: LevelData, cell: Point): void {
    const world = gridToWorld(cell, level.grid);
    for (let index = 0; index < 3; index += 1) {
      const dimensions = {
        width: 0.32 + index * 0.08,
        height: 0.22 + index * 0.04,
        depth: 0.48,
      };
      const rubble = MeshBuilder.CreateBox(
        `brick-rubble-${cell.x}-${cell.y}-${index}`,
        dimensions,
        this.scene,
      );
      rubble.position.set(world.x - 0.55 + index * 0.42, 0.12 + index * 0.02, world.z + 0.55);
      rubble.rotation.y = index * 0.71;
      applyBoxQuadUvs(rubble, dimensions);
      rubble.material = this.materials.get("medievalBrickDamaged");
      rubble.metadata = { surface: "rubble" };
    }
  }

  private createMissionLamp(level: LevelData, cell: Point, color: "amber" | "red"): void {
    const world = gridToWorld(cell, level.grid);
    const dimensions = { width: 0.7, height: 0.24, depth: 0.48 };
    const housing = MeshBuilder.CreateBox(
      `mission-lamp-${color}-${cell.x}-${cell.y}`,
      dimensions,
      this.scene,
    );
    housing.position.set(world.x, 2.7, world.z);
    applyBoxQuadUvs(housing, dimensions, 0.7, 0.24);
    housing.material = this.materials.get(
      color === "amber" ? "amberTerminal" : "hazardPlateMuted",
      true,
    );
    const light = new PointLight(
      `mission-light-${color}-${cell.x}-${cell.y}`,
      new Vector3(world.x, 2.35, world.z),
      this.scene,
    );
    light.diffuse =
      color === "amber" ? new Color3(0.86, 0.42, 0.12) : new Color3(0.82, 0.06, 0.025);
    light.specular = Color3.Black();
    light.intensity = color === "amber" ? 0.42 : 0.58;
    light.range = color === "amber" ? 9 : 8;
    this.decorativeLights.push(light);
  }

  private createReactorArchitecture(level: LevelData): void {
    const reactor = level.mission?.landmarks.find((landmark) => landmark.id === "ritual_reactor");
    if (!reactor) throw new Error("Mission level is missing the ritual reactor landmark");
    const reactorCenter = gridToWorld(reactor.center, level.grid);
    for (const [offsetX, offsetZ] of [
      [-5.5, -5.5],
      [5.5, -5.5],
      [-5.5, 5.5],
      [5.5, 5.5],
    ] as const) {
      const pillar = MeshBuilder.CreateCylinder(
        "ritual-reactor-pillar",
        { diameter: 2.5, height: WALL_HEIGHT, tessellation: 10 },
        this.scene,
      );
      pillar.position.set(reactorCenter.x + offsetX, WALL_HEIGHT / 2, reactorCenter.z + offsetZ);
      pillar.material = this.materials.get("brickTechJunction");
      pillar.checkCollisions = true;
      pillar.metadata = { surface: "reactor-pillar" };
      this.colliders.push(pillar);

      const conduit = MeshBuilder.CreateCylinder(
        "ritual-reactor-conduit",
        { diameter: 0.46, height: 2.8, tessellation: 8 },
        this.scene,
      );
      conduit.position.set(reactorCenter.x + offsetX, 1.45, reactorCenter.z + offsetZ - 1.05);
      conduit.material = this.materials.get("greenEnergyChannel", true);
      conduit.metadata = { surface: "reactor-conduit", missionEnergy: true };
    }
    const greenLight = new PointLight(
      "ritual-reactor-light",
      new Vector3(reactorCenter.x, 1.6, reactorCenter.z),
      this.scene,
    );
    greenLight.diffuse = new Color3(0.2, 0.82, 0.28);
    greenLight.specular = Color3.Black();
    greenLight.intensity = 0.78;
    greenLight.range = 18;
    this.decorativeLights.push(greenLight);
  }

  private createFoundryArchitecture(level: LevelData): void {
    const pressCell = level.mission?.objects.find(
      (object) => object.kind === "foundry_press",
    )?.cell;
    if (!pressCell) throw new Error("Mission level is missing the foundry press");
    const center = gridToWorld(pressCell, level.grid);
    for (const offsetX of [-2.4, 2.4]) {
      const dimensions = { width: 0.65, height: 3.2, depth: 2.7 };
      const support = MeshBuilder.CreateBox("foundry-press-support", dimensions, this.scene);
      support.position.set(center.x + offsetX, 1.6, center.z);
      applyBoxQuadUvs(support, dimensions);
      support.material = this.materials.get("ritualReactorCasing");
      support.checkCollisions = true;
      support.metadata = { surface: "foundry-press" };
      this.colliders.push(support);
    }
    const pressDimensions = { width: 4.3, height: 0.55, depth: 2.3 };
    const press = MeshBuilder.CreateBox("foundry-press-head", pressDimensions, this.scene);
    press.position.set(center.x, 2.65, center.z);
    applyBoxQuadUvs(press, pressDimensions);
    press.material = this.materials.get("hazardPlateMuted");
    press.metadata = { surface: "foundry-press" };
  }

  private createPortalFrame(level: LevelData, position: Point, orientation: "x" | "y"): void {
    const world = gridToWorld(position, level.grid);
    const halfSpan = 3.6;
    for (const offset of [-halfSpan, halfSpan]) {
      const dimensions =
        orientation === "x"
          ? { width: 0.45, height: 3.5, depth: 0.7 }
          : { width: 0.7, height: 3.5, depth: 0.45 };
      const post = MeshBuilder.CreateBox("riveted-portal-post", dimensions, this.scene);
      post.position.set(
        world.x + (orientation === "x" ? offset : 0),
        1.75,
        world.z + (orientation === "y" ? offset : 0),
      );
      applyBoxQuadUvs(post, dimensions, 0.7, 3.5);
      post.material = this.materials.get("rivetedBlastDoor");
    }
    const lintelDimensions =
      orientation === "x"
        ? { width: halfSpan * 2 + 0.8, height: 0.48, depth: 0.7 }
        : { width: 0.7, height: 0.48, depth: halfSpan * 2 + 0.8 };
    const lintel = MeshBuilder.CreateBox("riveted-portal-lintel", lintelDimensions, this.scene);
    lintel.position.set(world.x, 3.28, world.z);
    applyBoxQuadUvs(lintel, lintelDimensions);
    lintel.material = this.materials.get("brickArchTrim");
  }

  private createMissionTerminal(level: LevelData, cell: Point): void {
    const world = gridToWorld(cell, level.grid);
    const dimensions = { width: 0.75, height: 1.1, depth: 0.28 };
    const terminal = MeshBuilder.CreateBox("amber-wall-terminal", dimensions, this.scene);
    terminal.position.set(world.x, 1.1, world.z);
    applyBoxQuadUvs(terminal, dimensions, 0.75, 1.1);
    terminal.material = this.materials.get("amberTerminal", true);
    terminal.metadata = { surface: "terminal" };
  }

  private isDecorationSpotFree(level: LevelData, cell: Point): boolean {
    const reserved = [
      level.playerStart,
      level.exit,
      ...level.pickups.map((pickup) => pickup.cell),
      ...level.enemies.map((enemy) => enemy.cell),
      ...level.doors.flatMap((door) => door.cells),
    ];
    if (
      reserved.some((point) => Math.abs(point.x - cell.x) <= 1 && Math.abs(point.y - cell.y) <= 1)
    )
      return false;
    for (let y = cell.y - 1; y <= cell.y + 1; y += 1) {
      for (let x = cell.x - 1; x <= cell.x + 1; x += 1) {
        if (level.grid.get({ x, y }) !== TileType.Floor) return false;
      }
    }
    return true;
  }

  private createDecorations(level: LevelData): void {
    for (const prop of level.props ?? []) {
      const world = gridToWorld(prop.cell, level.grid);
      const dimensions = { width: CELL_SIZE, depth: CELL_SIZE, height: prop.kind === "pillar" ? WALL_HEIGHT : 1.7 };
      const mesh = MeshBuilder.CreateBox(prop.id, dimensions, this.scene);
      mesh.position.set(world.x, dimensions.height / 2, world.z);
      applyBoxQuadUvs(mesh, dimensions, 2, prop.kind === "pillar" ? 3.6 : 1.7);
      mesh.material = this.materials.get(prop.kind === "pillar" ? "grimyStoneBlocks" : "crate");
      mesh.checkCollisions = true;
      mesh.isPickable = true;
      mesh.metadata = {surface:prop.kind,propId:prop.id};
      this.colliders.push(mesh);
    }
    const hub = this.getRoom(level, "hub");
    this.createComputerBank(level, { x: hub.x + 1, y: hub.y + 1 });
    this.createComputerBank(level, { x: hub.x + hub.width - 2, y: hub.y + 1 });

    const toxic = this.getRoom(level, "toxic");
    for (const offset of [-4, 4]) {
      const cell = { x: toxic.center.x + offset, y: toxic.center.y + (offset > 0 ? -3 : 3) };
      this.createTank(level, cell);
    }

    const supply = this.getRoom(level, "supply");
    this.createCrate(level, { x: supply.x + 1, y: supply.y + 1 }, 0);
    this.createCrate(level, { x: supply.x + 2, y: supply.y + 1 }, Math.PI / 2);

    for (const room of level.rooms.filter((candidate) =>
      ["hub", "toxic", "dark", "finale"].includes(candidate.id),
    )) {
      this.createLamp(level, room.center, room.id === "dark");
    }
  }

  private createComputerBank(level: LevelData, cell: Point): void {
    if (!this.isDecorationSpotFree(level, cell)) return;
    const world = gridToWorld(cell, level.grid);
    const dimensions = { width: 1.8, height: 1.4, depth: 0.5 };
    const mesh = MeshBuilder.CreateBox("computer-bank", dimensions, this.scene);
    mesh.position.set(world.x, 0.7, world.z);
    applyBoxQuadUvs(mesh, dimensions, 1.8, 1.4);
    mesh.material = this.materials.get("computerPanel", true);
    mesh.checkCollisions = true;
    mesh.metadata = { surface: "computer" };
    this.colliders.push(mesh);
  }

  private createTank(level: LevelData, cell: Point): void {
    if (!this.isDecorationSpotFree(level, cell)) return;
    const world = gridToWorld(cell, level.grid);
    const tank = MeshBuilder.CreateCylinder(
      "coolant-tank",
      { diameter: 2.4, height: 2.7, tessellation: 10 },
      this.scene,
    );
    tank.position.set(world.x, 1.35, world.z);
    tank.material = this.materials.get("toxicFloor", true);
    tank.checkCollisions = true;
    tank.metadata = { surface: "tank" };
    this.colliders.push(tank);
  }

  private createCrate(level: LevelData, cell: Point, rotationY: number): void {
    if (!this.isDecorationSpotFree(level, cell)) return;
    const world = gridToWorld(cell, level.grid);
    const dimensions = { width: 1.4, height: 1.4, depth: 1.4 };
    const crate = MeshBuilder.CreateBox("crate", dimensions, this.scene);
    crate.position.set(world.x, 0.7, world.z);
    crate.rotation.y = rotationY;
    applyBoxQuadUvs(crate, dimensions, 1.4, 1.4);
    crate.material = this.materials.get("crate");
    crate.checkCollisions = true;
    crate.metadata = { surface: "crate" };
    this.colliders.push(crate);
  }

  private createLamp(level: LevelData, cell: Point, red: boolean): void {
    const world = gridToWorld(cell, level.grid);
    const dimensions = { width: 1.2, height: 0.12, depth: 0.45 };
    const housing = MeshBuilder.CreateBox("ceiling-lamp", dimensions, this.scene);
    housing.position.set(world.x, WALL_HEIGHT - 0.12, world.z);
    applyBoxQuadUvs(housing, dimensions, 1.2, 0.45);
    const material = new StandardMaterial(`lamp-${cell.x}-${cell.y}`, this.scene);
    material.emissiveColor = red ? new Color3(0.8, 0.04, 0.02) : new Color3(0.12, 0.65, 0.52);
    material.diffuseColor = Color3.Black();
    housing.material = material;
    const light = new PointLight(
      `light-${cell.x}-${cell.y}`,
      new Vector3(world.x, 3.1, world.z),
      this.scene,
    );
    light.diffuse = red ? Color3.FromHexString(PALETTE.red) : Color3.FromHexString(PALETTE.cyan);
    light.specular = Color3.Black();
    light.intensity = red ? 0.8 : 0.55;
    light.range = 10;
    this.decorativeLights.push(light);
  }

  private collectTileRuns(
    grid: GridMap,
    tile: TileType,
  ): Array<{ y: number; startX: number; length: number }> {
    const runs: Array<{ y: number; startX: number; length: number }> = [];
    for (let y = 0; y < grid.height; y += 1) {
      let startX = -1;
      for (let x = 0; x <= grid.width; x += 1) {
        if (x < grid.width && grid.get({ x, y }) === tile) {
          if (startX < 0) startX = x;
        } else if (startX >= 0) {
          runs.push({ y, startX, length: x - startX });
          startX = -1;
        }
      }
    }
    return runs;
  }

  private getRoom(level: LevelData, id: Room["id"]): Room {
    const room = level.rooms.find((candidate) => candidate.id === id);
    if (!room) throw new Error(`Missing room ${id}`);
    return room;
  }
}
